'use client';

import { useEffect, useMemo, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

import {
  Building,
  Lock,
  Image,
  Trash2,
  Globe,
  Pencil,
  Star,
  Sparkles,
  Mail,
} from 'lucide-react';

import Joyride from 'react-joyride';
import { Card, CardContent } from '@/components/ui/card';
import { getTourStyles } from '@/components/tour/tourStyles';
import ScheduleDialog from '@/components/pages/dialogs/ScheduleDialog';
import HolidayDialog from '@/components/pages/dialogs/HolidayDialog';
import ChangePasswordDialog from '@/components/pages/dialogs/ChangePasswordDialog';
import LogoCropDialog from '@/components/pages/dialogs/LogoCropDialog';
import UnsavedChangesDialog from '@/components/pages/dialogs/UnsavedChangesDialog';
import AddRedDialog from '@/components/pages/dialogs/AddRedDialog';
import { GenerateDescriptionDialog } from '@/components/pages/dialogs/GenerateDescriptionDialog';

import { compressImage } from '@/lib/imageCompression';

import CompanyProfileTourSteps from '@/components/tour/company/CompanyProfileTourSteps';

import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import { useEmpresa } from '@/components/hooks/useEmpresa';
import { useCategoriasEmpresa } from '@/components/hooks/useCategoriasEmpresa';
import { useSubcategorias } from '@/components/hooks/useSubcategorias';
import { useEmpresaSubcategorias } from '@/components/hooks/useEmpresaSubcategorias';
import { useUploadEmpresaFotos } from '@/components/hooks/useUploadEmpresaFotos';
import { useUploadEmpresaLogo } from '@/components/hooks/useUploadEmpresaLogo';
import { useRedesSociales } from '@/components/hooks/useRedesSociales';
import { useIA } from '@/components/hooks/useIA';
import { useUnsavedChanges } from '@/components/hooks/useUnsavedChanges';
import { usePageHelp, useTourVisibility } from '@/components/hooks/usePageHelp';
import type { RedEmpresa } from '@/components/hooks/useRedesSociales';

import SubcategoriaMultiSelect from '@/components/ui/SubcategoriaMultiSelect';
import { GLASS_CARD } from '@/components/ui/glass';

const containerVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { staggerChildren: 0.06 } },
};
const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.35 } },
};

export default function CompanyProfile() {
  const { t } = useTranslation();

  const id_empresa = localStorage.getItem('id_empresa') || undefined;
  const id_usuario = localStorage.getItem('id_usuario') || undefined;

  const { empresa, loadingEmpresa, updateEmpresaLocal, saveEmpresa } =
    useEmpresa(id_empresa);
  const { categorias } = useCategoriasEmpresa();
  const { uploadFotos, deleteFoto, setFotoDestacada, quitarFotoDestacada } =
    useUploadEmpresaFotos({
      id_empresa: id_empresa!,
      id_usuario: id_usuario!,
      getFotos: () => empresa?.fotos ?? [],
      updateEmpresaLocal,
    });
  const { uploadLogo, loadingLogo } = useUploadEmpresaLogo({
    empresa: empresa!,
    updateEmpresaLocal,
  });

  const { generarDescripcion, loading: loadingIA } = useIA();

  const esDiamante = empresa?.es_fundador_diamante === true;
  const esPlatino = empresa?.es_fundador_platino === true;
  const esPremium = esDiamante || esPlatino;

  const isMobile = window.innerWidth < 640;
  const [rawLogoFile, setRawLogoFile] = useState<File | null>(null);
  const [openLogoCrop, setOpenLogoCrop] = useState(false);
  const [runTour, setRunTour] = useState(false);
  const [showRedDialog, setShowRedDialog] = useState(false);
  const [redToEdit, setRedToEdit] = useState<RedEmpresa | null>(null);
  const [showIADialog, setShowIADialog] = useState(false);
  const [iaDescripcion, setIaDescripcion] = useState('');
  const { deleteRed } = useRedesSociales(id_empresa);
  const [uploadingFotos, setUploadingFotos] = useState(false);

  // El botón global de ayuda (arriba a la derecha) lanza el tour de esta
  // página. Se oculta solo mientras carga, que es cuando no hay nada que
  // enseñar todavía.
  usePageHelp(() => setRunTour(true), !!id_empresa && !loadingEmpresa);
  useTourVisibility(runTour);

  const [form, setForm] = useState({
    empresa: '',
    descripcion: '',
    direccion: '',
    telefono: '',
    email: '',
    pais: '',
    ciudad: '',
    localidad: '',
    id_categoria: '',
  });

  // Subcategorías: opciones disponibles según la categoría elegida
  const { subcategorias } = useSubcategorias(form.id_categoria || null);

  // Función para guardar subcategorías (la lectura viene de
  // empresa.subcategorias, ya incluida en vista_empresa_detalle)
  const { guardarSubcategorias } = useEmpresaSubcategorias();
  const [subcategoriasSeleccionadas, setSubcategoriasSeleccionadas] = useState<
    string[]
  >([]);

  useEffect(() => {
    if (empresa) {
      setForm({
        empresa: empresa.empresa,
        descripcion: empresa.descripcion || '',
        direccion: empresa.direccion || '',
        telefono: empresa.telefono || '',
        email: empresa.email || '',
        pais: empresa.pais || '',
        ciudad: empresa.ciudad || '',
        localidad: empresa.localidad || '',
        id_categoria: empresa.id_categoria,
      });
    }
  }, [empresa]);

  // Se sincroniza solo al cargar la empresa por primera vez (id_empresa
  // no cambia durante la sesión), no en cada actualización local del
  // objeto empresa — si no, el selector "parpadearía" al guardar, porque
  // saveEmpresa actualiza la caché local antes de que termine de guardarse
  // la propia subcategoría.
  useEffect(() => {
    if (empresa) {
      setSubcategoriasSeleccionadas(
        (empresa.subcategorias ?? []).map((s) => s.id_subcategoria)
      );
    }
  }, [empresa?.id_empresa]);

  // ── Detección de cambios sin guardar (solo datos de empresa) ──
  const hayCambios = useMemo(() => {
    if (!empresa) return false;

    const formCambiado =
      form.empresa !== empresa.empresa ||
      form.descripcion !== (empresa.descripcion || '') ||
      form.direccion !== (empresa.direccion || '') ||
      form.telefono !== (empresa.telefono || '') ||
      form.pais !== (empresa.pais || '') ||
      form.ciudad !== (empresa.ciudad || '') ||
      form.localidad !== (empresa.localidad || '') ||
      form.id_categoria !== empresa.id_categoria;

    const idsOriginales = (empresa.subcategorias ?? [])
      .map((s) => s.id_subcategoria)
      .sort();
    const idsActuales = [...subcategoriasSeleccionadas].sort();
    const subcatCambiadas =
      idsOriginales.length !== idsActuales.length ||
      idsOriginales.some((id, i) => id !== idsActuales[i]);

    return formCambiado || subcatCambiadas;
  }, [empresa, form, subcategoriasSeleccionadas]);

  const { promptOpen, confirmLeave, cancelLeave } = useUnsavedChanges({
    when: hayCambios,
  });

  const handleSave = async () => {
    if (!empresa) return;
    const { error } = await saveEmpresa({
      empresa: form.empresa,
      descripcion: form.descripcion,
      direccion: form.direccion,
      telefono: form.telefono,
      pais: form.pais,
      ciudad: form.ciudad,
      localidad: form.localidad,
      id_categoria: form.id_categoria,
    });
    if (error) {
      toast.error('Error al guardar la descripción');
      return;
    }

    const idsAnteriores = (empresa.subcategorias ?? []).map(
      (s) => s.id_subcategoria
    );
    const { error: errorSubcat } = await guardarSubcategorias(
      empresa.id_empresa,
      idsAnteriores,
      subcategoriasSeleccionadas
    );
    if (errorSubcat) {
      console.error('Error al guardar subcategorías:', errorSubcat);
      toast.error('Error al guardar las subcategorías');
      return;
    }

    // Actualizamos la caché local para no depender de un refetch
    updateEmpresaLocal({
      subcategorias: subcategorias.filter((s) =>
        subcategoriasSeleccionadas.includes(s.id_subcategoria)
      ),
    });

    window.dispatchEvent(new CustomEvent('descripcion-guardada'));
  };

  const handleInput = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { id, value } = e.target;
    setForm((p) => ({ ...p, [id]: value }));
  };

  const handleCategoriaChange = (value: string) => {
    setForm((p) => ({ ...p, id_categoria: value }));
    // Si cambia la categoría, las subcategorías de la anterior ya no aplican
    setSubcategoriasSeleccionadas([]);
  };

  const handleGenerarDescripcion = async () => {
    const resultado = await generarDescripcion('descripcion_empresa', {
      id_empresa,
    });
    setIaDescripcion(resultado);
    setShowIADialog(true);
  };

  function ensureFile(blob: Blob, originalFile: File): File {
    if (blob instanceof File) return blob;
    return new File([blob], originalFile.name.replace(/\.[^/.]+$/, '.jpg'), {
      type: blob.type || 'image/jpeg',
      lastModified: Date.now(),
    });
  }

  async function addWatermark(file: File): Promise<File> {
    const img = new window.Image();
    img.src = URL.createObjectURL(file);

    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = (e) => {
        console.error('[WM] image load error', e);
        reject(e);
      };
    });

    const logo = new window.Image();
    logo.crossOrigin = 'anonymous';
    logo.src = '/watermark.png';

    const logoLoaded = await new Promise<boolean>((resolve) => {
      logo.onload = () => resolve(true);
      logo.onerror = (e) => {
        console.warn('[WM] watermark not available, skipping', e);
        resolve(false);
      };
    });

    const canvas = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(img, 0, 0);

    if (logoLoaded) {
      const logoWidth = img.width * 0.2;
      const ratio = logo.height / logo.width;
      const logoHeight = logoWidth * ratio;
      const padding = 20;
      ctx.globalAlpha = 0.6;
      ctx.drawImage(
        logo,
        padding,
        img.height - logoHeight - padding,
        logoWidth,
        logoHeight
      );
    }

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            console.error('[WM] blob is null');
            reject(new Error('blob null'));
            return;
          }
          resolve(new File([blob], file.name, { type: 'image/jpeg' }));
        },
        'image/jpeg',
        0.9
      );
    });
  }

  const inputClass =
    'w-full border rounded-xl px-3 py-2 text-sm border-gray-300/70 dark:border-white/10 bg-white/70 dark:bg-black/40 supports-[backdrop-filter]:backdrop-blur-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:border-[#103254] focus:ring-1 focus:ring-[#103254] transition';

  if (!id_empresa || loadingEmpresa) {
    return (
      <div className="flex justify-center items-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-500 dark:border-gray-400 border-t-transparent dark:border-t-transparent" />
      </div>
    );
  }

  if (!empresa) return null;

  return (
    <motion.div
      className="w-full space-y-6 pb-28 lg:pb-0"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      {/* Header — el botón de ayuda vive ahora arriba a la derecha, fijo
          y global (HelpButton), lanzado desde usePageHelp. */}
      <motion.div variants={itemVariants}>
        <div className="tour-title">
          <span className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
            {t('companyProfile.title')}
          </span>
        </div>
      </motion.div>

      <Joyride
        steps={CompanyProfileTourSteps}
        run={runTour}
        continuous
        showSkipButton
        spotlightClicks
        locale={{
          back: t('companyProfile.tour.back'),
          close: t('companyProfile.tour.close'),
          last: t('companyProfile.tour.last'),
          next: t('companyProfile.tour.next'),
          skip: t('companyProfile.tour.skip'),
        }}
        callback={({ status }) => {
          if (status === 'finished' || status === 'skipped') setRunTour(false);
        }}
        styles={getTourStyles(isMobile)}
      />

      {/* Logo + Nombre */}
      <Card className={`rounded-2xl ${GLASS_CARD}`}>
        <CardContent className="p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
          {/* Fila logo+nombre: siempre juntos en flex-row */}
          <div className="flex flex-row items-center gap-4 flex-1 min-w-0">
            <label
              htmlFor="logoUpload"
              className="relative w-20 h-20 flex-shrink-0 cursor-pointer tour-logo"
            >
              <div className="w-full h-full rounded-full overflow-hidden border border-gray-300 dark:border-gray-600 hover:border-[#103254] dark:hover:border-blue-400 transition-colors flex items-center justify-center">
                {empresa.logo ? (
                  <img
                    src={`${empresa.logo}?t=${Date.now()}`}
                    className="object-cover w-full h-full"
                  />
                ) : (
                  <Building className="w-10 h-10 text-gray-400" />
                )}
              </div>

              {esPremium && (
                <span
                  className="absolute -bottom-1 -right-1 text-2xl flex items-center justify-center z-10"
                  title={esDiamante ? 'Fundador Diamante' : 'Fundador Platino'}
                >
                  {esDiamante ? '💎' : '🪙 '}
                </span>
              )}

              <input
                id="logoUpload"
                type="file"
                accept="image/*"
                className="hidden"
                disabled={loadingLogo}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  setRawLogoFile(file);
                  setOpenLogoCrop(true);
                  e.target.value = '';
                }}
              />
            </label>

            <div className="flex flex-col justify-center min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white truncate">
                  {form.empresa}
                </h2>
                {esPremium && (
                  <span
                    className={`shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full
              ${
                esDiamante
                  ? 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300'
                  : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
              }`}
                  >
                    {esDiamante ? '💎 Diamante' : '🪙 Platino'}
                  </span>
                )}
              </div>
              <p className="text-gray-600 dark:text-gray-400 text-sm truncate">
                {form.email}
              </p>
            </div>
          </div>

          {/* Botones: mitad de ancho cada uno en móvil, agrupados a la derecha en PC */}
          <div className="flex gap-2 sm:flex-shrink-0">
            <div className="flex-1 sm:flex-none tour-schedule-btn">
              <ScheduleDialog id_empresa={id_empresa} />
            </div>
            <div className="flex-1 sm:flex-none tour-holiday-btn">
              <HolidayDialog id_empresa={id_empresa} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Datos empresa */}
      <Card className={`rounded-2xl ${GLASS_CARD}`}>
        <CardContent className="p-6 space-y-6 tour-inputs">
          <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400 mb-2">
            <Building className="w-5 h-5" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t('companyProfile.companyData.sectionTitle')}
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label className="text-gray-700 dark:text-gray-300">
                {t('companyProfile.companyData.labelName')}
              </Label>
              <Input
                id="empresa"
                value={form.empresa}
                onChange={handleInput}
                className={inputClass}
              />
            </div>

            <div>
              <Label className="text-gray-700 dark:text-gray-300">
                {t('companyProfile.companyData.labelEmail')}
              </Label>
              <Input
                id="email"
                disabled
                value={form.email}
                className={`${inputClass} disabled:bg-gray-100 dark:disabled:bg-white/5`}
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-gray-700 dark:text-gray-300">
                  {t('companyProfile.companyData.labelCategory')}
                </Label>
              </div>
              <Select
                value={form.id_categoria}
                onValueChange={handleCategoriaChange}
              >
                <SelectTrigger className={inputClass}>
                  <SelectValue
                    placeholder={t(
                      'companyProfile.companyData.placeholderCategory'
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  {categorias.map((c) => (
                    <SelectItem key={c.id_categoria} value={c.id_categoria}>
                      {c.categoria}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-0.5 mb-1">
                <Label className="text-gray-700 dark:text-gray-300">
                  Subcategorías
                </Label>
                <span className="text-xs text-gray-400 dark:text-gray-500">
                  Opcional — ayuda a que te encuentren mejor
                </span>
              </div>
              <SubcategoriaMultiSelect
                opciones={subcategorias}
                seleccionadas={subcategoriasSeleccionadas}
                onChange={setSubcategoriasSeleccionadas}
                disabled={!form.id_categoria}
                className={inputClass}
              />
            </div>

            <div>
              <Label className="text-gray-700 dark:text-gray-300">
                {t('companyProfile.companyData.labelPhone')}
              </Label>
              <Input
                id="telefono"
                value={form.telefono}
                onChange={handleInput}
                className={inputClass}
              />
            </div>

            <div>
              <Label className="text-gray-700 dark:text-gray-300">
                {t('companyProfile.companyData.labelCountry')}
              </Label>
              <Input
                id="pais"
                value={form.pais}
                onChange={handleInput}
                className={inputClass}
              />
            </div>

            <div>
              <Label className="text-gray-700 dark:text-gray-300">
                {t('companyProfile.companyData.labelCity')}
              </Label>
              <Input
                id="ciudad"
                value={form.ciudad}
                onChange={handleInput}
                className={inputClass}
              />
            </div>

            <div>
              <Label className="text-gray-700 dark:text-gray-300">
                {t('companyProfile.companyData.labelLocality')}
              </Label>
              <Input
                id="localidad"
                value={form.localidad}
                onChange={handleInput}
                className={inputClass}
              />
            </div>

            <div className="col-span-full">
              <Label className="text-gray-700 dark:text-gray-300">
                {t('companyProfile.companyData.labelAddress')}
              </Label>
              <Input
                id="direccion"
                value={form.direccion}
                onChange={handleInput}
                className={inputClass}
              />
            </div>

            <div className="col-span-full">
              <div className="flex items-center justify-between mb-1">
                <Label className="text-gray-700 dark:text-gray-300">
                  {t('companyProfile.companyData.labelDescription')}
                </Label>
                <button
                  type="button"
                  onClick={handleGenerarDescripcion}
                  disabled={loadingIA}
                  className="tour-ia-btn flex items-center gap-1.5 text-xs font-medium text-[#103254] dark:text-blue-400 hover:text-[#0d2742] dark:hover:text-blue-300 border border-[#103254]/30 dark:border-blue-400/30 rounded-lg px-2.5 py-1 hover:bg-[#103254]/5 dark:hover:bg-blue-400/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  {loadingIA ? 'Generando…' : 'Generar con IA'}
                </button>
              </div>
              <Textarea
                id="descripcion"
                value={form.descripcion}
                onChange={handleInput}
                className={`${inputClass} resize-none`}
              />
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              onClick={handleSave}
              className="bg-[#103254] hover:bg-[#0d2742] dark:bg-[#103254] dark:hover:bg-[#0d2742] text-white rounded-xl px-5 py-2 tour-save-btn transition-colors"
            >
              {t('companyProfile.companyData.saveBtn')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Redes sociales */}
      <Card className={`rounded-2xl ${GLASS_CARD}`}>
        <CardContent className="p-6 space-y-6 tour-social">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400 mb-2">
              <Globe className="w-5 h-5" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('companyProfile.social.sectionTitle')}
              </h3>
            </div>
            <Button
              size="sm"
              onClick={() => {
                setRedToEdit(null);
                setShowRedDialog(true);
              }}
              className="bg-[#103254] hover:bg-[#0d2742] dark:bg-[#103254] dark:hover:bg-[#0d2742] text-white rounded-xl transition-colors"
            >
              {t('companyProfile.social.addBtn')}
            </Button>
          </div>

          {empresa?.redes_sociales?.length === 0 ? (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('companyProfile.social.empty')}
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {empresa.redes_sociales.map((red) => (
                <div
                  key={red.id_tipo_red}
                  className="flex items-center justify-between border border-white/40 dark:border-white/10 bg-white/50 dark:bg-white/5 supports-[backdrop-filter]:backdrop-blur-sm p-4 rounded-xl shadow-sm gap-4"
                >
                  <div className="flex flex-col overflow-hidden">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {red.nombre}
                    </span>
                    <a
                      href={red.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 dark:text-blue-400 hover:underline truncate"
                    >
                      {red.url}
                    </a>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => {
                        setRedToEdit(red);
                        setShowRedDialog(true);
                      }}
                      className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white/70 dark:bg-black/40 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:border-blue-300 dark:hover:border-blue-600 transition-colors"
                    >
                      <Pencil className="w-4 h-4 text-[#103254] dark:text-blue-400" />
                    </button>
                    <button
                      onClick={async () => {
                        const { error } = await deleteRed(red.id_tipo_red);
                        if (!error) {
                          updateEmpresaLocal({
                            redes_sociales: empresa.redes_sociales.filter(
                              (r) => r.id_tipo_red !== red.id_tipo_red
                            ),
                          });
                        }
                      }}
                      className="p-2 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/40 hover:border-red-300 dark:hover:border-red-700 transition-colors"
                    >
                      <Trash2 className="w-4 h-4 text-red-500 dark:text-red-400" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <AddRedDialog
            open={showRedDialog}
            onOpenChange={(open) => {
              setShowRedDialog(open);
              if (!open) setRedToEdit(null);
            }}
            id_empresa={id_empresa}
            redToEdit={redToEdit}
            onSaved={(red) => {
              const existe = empresa.redes_sociales.some(
                (r) => r.id_tipo_red === red.id_tipo_red
              );
              let nuevas;
              if (existe) {
                nuevas = empresa.redes_sociales.map((r) =>
                  r.id_tipo_red === red.id_tipo_red ? red : r
                );
                toast.success(t('companyProfile.social.toastUpdated'));
              } else {
                nuevas = [...empresa.redes_sociales, red];
                toast.success(t('companyProfile.social.toastAdded'));
              }
              updateEmpresaLocal({ redes_sociales: nuevas });
            }}
          />
        </CardContent>
      </Card>

      {/* Fotos */}
      <Card className={`rounded-2xl ${GLASS_CARD}`}>
        <CardContent className="p-6 space-y-4 tour-add-photo">
          <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
            <Image className="w-5 h-5" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t('companyProfile.photos.sectionTitle')}
            </h3>
          </div>

          <p className="flex items-center gap-2 text-sm text-gray-500">
            <span className="w-2 h-2 rounded-full bg-[#26857A]" />
            {t('companyProfile.photos.hint')}
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {[...empresa.fotos]
              .sort((a, b) => Number(b.destacado) - Number(a.destacado))
              .map((foto) => (
                <div
                  key={foto.id_foto}
                  className={`relative w-full h-32 rounded-xl overflow-hidden shadow-sm group border-4 shadow-[#103254]/30 ${
                    foto.destacado
                      ? 'border-[#26857A]'
                      : 'border-gray-200 dark:border-gray-700'
                  }`}
                >
                  <img
                    src={foto.url_foto}
                    className="object-cover w-full h-full"
                  />
                  {foto.destacado ? (
                    <button
                      onClick={() => quitarFotoDestacada(foto.id_foto)}
                      className="absolute bottom-2 left-2 p-1.5 rounded-full shadow transition bg-[#26857A] opacity-0 group-hover:opacity-100 hover:bg-red-500"
                      title="Quitar imagen principal"
                    >
                      <Star className="w-4 h-4 text-white" />
                    </button>
                  ) : (
                    <button
                      onClick={() => setFotoDestacada(foto.id_foto)}
                      className="absolute bottom-2 left-2 p-1.5 rounded-full shadow transition bg-white/90 backdrop-blur opacity-0 group-hover:opacity-100 hover:bg-[#103254]"
                      title="Marcar como imagen principal"
                    >
                      <Star className="w-4 h-4 text-gray-600 hover:text-white" />
                    </button>
                  )}
                  <button
                    onClick={() => deleteFoto(foto)}
                    className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 p-1 rounded-full text-white opacity-0 group-hover:opacity-100 transition-all"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}

            <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl h-32 cursor-pointer hover:bg-gray-50/60 dark:hover:bg-gray-900/40 hover:border-gray-400 dark:hover:border-gray-500 transition-colors">
              {uploadingFotos ? (
                <div className="flex flex-col items-center gap-2">
                  <div className="animate-spin rounded-full h-6 w-6 border-2 border-[#103254] border-t-transparent" />
                  <span className="text-xs text-gray-400">Subiendo...</span>
                </div>
              ) : (
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {empresa.fotos.length >= 10
                    ? t('companyProfile.photos.maxReached')
                    : t('companyProfile.photos.addPhotos')}
                </span>
              )}
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                disabled={empresa.fotos.length >= 10 || uploadingFotos}
                onChange={async (e) => {
                  if (!e.target.files) return;
                  const originalFiles = Array.from(e.target.files).filter((f) =>
                    f.type.startsWith('image/')
                  );
                  const galeriaVacia = empresa.fotos.length === 0;
                  setUploadingFotos(true);
                  try {
                    const processedFiles = await Promise.all(
                      originalFiles.map(async (file) => {
                        const watermarked = await addWatermark(file);
                        const compressedBlob = await compressImage(
                          watermarked,
                          {
                            maxSizeMB: 1,
                            maxWidthOrHeight: 1600,
                          }
                        );
                        return ensureFile(compressedBlob, file);
                      })
                    );
                    const dataTransfer = new DataTransfer();
                    processedFiles.forEach((file) =>
                      dataTransfer.items.add(file)
                    );
                    await uploadFotos(dataTransfer.files, galeriaVacia);
                    window.dispatchEvent(new CustomEvent('fotos-actualizadas'));
                  } catch (error) {
                    console.error('[UPLOAD] error', error);
                    toast.error(t('companyProfile.photos.errorProcess'));
                  } finally {
                    setUploadingFotos(false);
                    e.target.value = '';
                  }
                }}
              />
            </label>
          </div>
        </CardContent>
      </Card>

      {/* Beneficios Fundador */}
      {esPremium && (
        <Card className={`rounded-2xl ${GLASS_CARD} tour-founder`}>
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {esDiamante ? '💎 Fundador Diamante' : '🪙 Fundador Platino'}
              </h3>
            </div>

            <p className="text-sm text-gray-500 dark:text-gray-400">
              {esDiamante
                ? 'Tienes acceso 100% gratuito de por vida a todas las funcionalidades de Bookoll. Eres uno de los primeros negocios en confiar en nosotros.'
                : 'Tienes un 50% de descuento de por vida en tu suscripción a Bookoll. Gracias por ser uno de los primeros en confiar en nosotros.'}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                {
                  label: 'Precio',
                  value: esDiamante
                    ? 'Gratis de por vida'
                    : '50% descuento de por vida',
                },
                {
                  label: 'Acceso prioritario',
                  value: 'Nuevas funciones en primicia',
                },
                { label: 'Soporte', value: 'Canal directo con el equipo' },
                { label: 'Estado', value: 'Activo ✓' },
              ].map(({ label, value }) => (
                <div
                  key={label}
                  className={`rounded-xl p-3 border text-sm
                    ${
                      esDiamante
                        ? 'border-cyan-200 bg-cyan-50/70 dark:border-cyan-800 dark:bg-cyan-900/20'
                        : 'border-gray-200 bg-gray-50/70 dark:border-gray-700 dark:bg-white/5'
                    }`}
                >
                  <span className="block text-xs text-gray-400 dark:text-gray-500 mb-0.5">
                    {label}
                  </span>
                  <span
                    className={`font-medium ${
                      esDiamante
                        ? 'text-cyan-700 dark:text-cyan-300'
                        : 'text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {value}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Seguridad */}
      <Card className={`rounded-2xl ${GLASS_CARD}`}>
        <CardContent className="p-6 space-y-6 tour-security">
          <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400 mb-2">
            <Lock className="w-5 h-5" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t('companyProfile.security.sectionTitle')}
            </h3>
          </div>
          <ChangePasswordDialog />
        </CardContent>
      </Card>

            {/* Baja del negocio — no es autoservicio: desactivar el perfil no
          desactiva la empresa (seguiría publicada, aceptando reservas y con
          la suscripción activa), así que la tramitamos a mano. */}
      <Card className={`rounded-2xl ${GLASS_CARD}`}>
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400 mb-2">
            <Trash2 className="w-5 h-5" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Dar de baja el negocio
            </h3>
          </div>

          <p className="text-sm leading-relaxed text-gray-500 dark:text-gray-400">
            Dar de baja un negocio implica retirar su página pública, cerrar las
            reservas online y cancelar la suscripción. Lo gestionamos contigo
            para que no quede ninguna cita pendiente sin avisar.
          </p>

          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <Button
              variant="outline"
              onClick={() => {
                const subject = encodeURIComponent(
                  `Solicitud de baja — ${empresa.empresa}`
                );
                const body = encodeURIComponent(
                  `Hola,\n\nQuiero dar de baja mi negocio en Bookoll.\n\nNegocio: ${empresa.empresa}\nIdentificador: ${empresa.id_empresa}\nCorreo de contacto: ${form.email}\n\nGracias.`
                );
                window.location.href = `mailto:info@bookoll.com?subject=${subject}&body=${body}`;
              }}
              className="rounded-xl border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 dark:border-red-900/40 dark:text-red-400 dark:hover:bg-red-900/20"
            >
              <Mail className="mr-2 h-4 w-4" />
              Contactar con soporte
            </Button>

            <span className="text-sm text-gray-500 dark:text-gray-400">
              O escríbenos a{' '}
              <a
                href="mailto:info@bookoll.com"
                className="font-medium text-[#103254] dark:text-blue-400 underline underline-offset-2"
              >
                info@bookoll.com
              </a>
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Logo crop dialog */}
      {rawLogoFile && (
        <LogoCropDialog
          open={openLogoCrop}
          file={rawLogoFile}
          onCancel={() => {
            setOpenLogoCrop(false);
            setRawLogoFile(null);
          }}
          onSave={async (croppedFile) => {
            const compressedBlob = await compressImage(croppedFile, {
              maxSizeMB: 0.3,
              maxWidthOrHeight: 512,
            });
            const compressedLogo = ensureFile(compressedBlob, croppedFile);
            await uploadLogo(compressedLogo);
            setOpenLogoCrop(false);
            setRawLogoFile(null);
            window.dispatchEvent(new CustomEvent('logo-guardado'));
          }}
        />
      )}

      {/* IA - Generar descripción dialog */}
      <GenerateDescriptionDialog
        open={showIADialog}
        onOpenChange={setShowIADialog}
        descripcion={iaDescripcion}
        loading={loadingIA}
        onAccept={() => {
          setForm((p) => ({ ...p, descripcion: iaDescripcion }));
          setShowIADialog(false);
        }}
        onRegenerate={handleGenerarDescripcion}
      />

      {/* Aviso de cambios sin guardar (reutilizable) */}
      <UnsavedChangesDialog
        open={promptOpen}
        onConfirm={confirmLeave}
        onCancel={cancelLeave}
      />
    </motion.div>
  );
}